import { useTheme } from '@emotion/react';
import { Close } from '@icon-park/react';
import cn from 'classnames';
import {
  cloneElement,
  type HTMLProps,
  type MemoExoticComponent,
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
} from 'react';

import { Typography } from '../typography/Typography';
import { TAG_HUES, TAG_SIZES, TAG_VARIANTS } from './constants';
import * as styles from './Tag.styles';

export type TagProps = Omit<
  HTMLProps<HTMLDivElement>,
  'ref' | 'children' | 'size' | 'label' | 'value'
> & {
  /** Tag label. */
  label?: ReactNode;
  /** Icon (left side of the text) from IconPark. */
  icon?: ReactElement;
  /** Tag color variant. */
  variant?: TAG_VARIANTS;
  /** Tag color variant independent from theme. Do not use variant and hue at the same time. */
  hue?: TAG_HUES;
  /** Tag size variant. */
  size?: TAG_SIZES;
  /** Light variant of the tag (lighter background color with dark text). */
  light?: boolean;
  // Value does not matter, only passed to onRemove.
  value?: any;
  /** Show a shadow. */
  withShadow?: boolean;
  /** Tag deletion function. */
  onRemove?: (value: any) => void;
};

export const TagBase = ({
  label = undefined,
  icon = undefined,
  variant = TAG_VARIANTS.PRIMARY,
  hue = undefined,
  size = TAG_SIZES.MEDIUM,
  light = false,
  value = undefined,
  withShadow = false,
  onRemove = undefined,
  className = undefined,
  ...props
}: TagProps) => {
  const theme = useTheme();

  const onRemoveProxy = useCallback(() => onRemove(value), [onRemove, value]);

  return (
    <div
      css={styles.tag}
      className={cn(className, hue || variant, { light, withShadow })}
      {...props}
    >
      {!!icon &&
        cloneElement(icon, {
          style: { marginRight: 8 },
          size: 16,
          fill: styles.getIconFill[hue || variant](theme, light),
        })}

      <Typography
        variant={
          size === TAG_SIZES.SMALL
            ? Typography.VARIANTS.BUTTON_SMALL_BOLD
            : Typography.VARIANTS.BUTTON_MEDIUM_BOLD
        }
      >
        {label}
      </Typography>

      {!!onRemove && (
        <button type="button" css={styles.removeButton} onClick={onRemoveProxy}>
          <Close size="12" />
        </button>
      )}
    </div>
  );
};

TagBase.displayName = 'Tag';

export const Tag: MemoExoticComponent<typeof TagBase> & {
  VARIANTS?: typeof TAG_VARIANTS;
} & { HUES?: typeof TAG_HUES } & { SIZES?: typeof TAG_SIZES } & {
  VARIANT_COLOR_GROUP_MAPPING?: typeof styles.VARIANT_COLOR_GROUP_MAPPING;
} = memo(TagBase);

Tag.VARIANTS = TAG_VARIANTS;
Tag.HUES = TAG_HUES;
Tag.VARIANT_COLOR_GROUP_MAPPING = styles.VARIANT_COLOR_GROUP_MAPPING;
Tag.SIZES = TAG_SIZES;
